import type { ThemePreference } from '../domain/entities/types';
import { resolveBibleBookOrder, validateBibleBookOrder, type BibleBookCode } from '../domain/bibleCanon';
import { settingsRepository } from '../repositories/dexie/repositories';
import { nowInstant } from '../utils/core';

export type ExplicitTheme = Exclude<ThemePreference, 'system'>;

export const resolveTheme = (preference: ThemePreference, systemDark: boolean): ExplicitTheme =>
  preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;

const systemPrefersDark = (): boolean => typeof matchMedia === 'function'
  && matchMedia('(prefers-color-scheme: dark)').matches;

export const settingsService = {
  async ensureInitialized(systemDark = systemPrefersDark()) {
    const settings = await settingsRepository.ensureInitialized();
    return settings.theme === 'system'
      ? settingsRepository.normalizeLegacyTheme(resolveTheme(settings.theme, systemDark))
      : settings;
  },

  async setTheme(theme: ExplicitTheme): Promise<void> {
    const settings = await settingsRepository.ensureInitialized();
    settings.theme = theme;
    settings.updatedAt = nowInstant();
    await settingsRepository.put(settings);
  },

  async setShowHomeHighlight(showHomeHighlight: boolean): Promise<void> {
    const settings = await settingsRepository.ensureInitialized();
    settings.showHomeHighlight = showHomeHighlight;
    settings.updatedAt = nowInstant();
    await settingsRepository.putHomeHighlightPreference(settings);
  },

  async getBibleBookOrder(): Promise<BibleBookCode[]> {
    const settings = await settingsRepository.ensureInitialized();
    return resolveBibleBookOrder(settings.bibleBookOrder);
  },

  async setBibleBookOrder(order: readonly BibleBookCode[]): Promise<void> {
    const settings = await settingsRepository.ensureInitialized();
    settings.bibleBookOrder = validateBibleBookOrder(order);
    settings.updatedAt = nowInstant();
    await settingsRepository.putBibleBookOrderPreference(settings);
  }
};
